import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

const POINTS = [
    {
        id: "adhipati",
        name: "Adhipati Marma",
        region: "Crown of the head",
        x: 100,
        y: 18,
        desc: "Classical texts regard this as a chief point, associated with the mind and the senses. Gentle work here is traditionally sought for a settled, quiet mind.",
    },
    {
        id: "sthapani",
        name: "Sthapani Marma",
        region: "Between the eyebrows",
        x: 100,
        y: 40,
        desc: "Traditionally linked with focus and inner stillness, this point is often included in sessions intended to ease a busy, restless mind.",
    },
    {
        id: "hridaya",
        name: "Hridaya Marma",
        region: "Centre of the chest",
        x: 100,
        y: 128,
        desc: "Named for the heart region, classical tradition regards it as a seat of vitality and calm, balanced breathing.",
    },
    {
        id: "nabhi",
        name: "Nabhi Marma",
        region: "Navel region",
        x: 100,
        y: 172,
        desc: "The navel region is described in Ayurvedic tradition as a centre connected with digestion and the distribution of energy through the body.",
    },
    {
        id: "manibandha",
        name: "Manibandha Marma",
        region: "Wrist joint",
        x: 44,
        y: 190,
        desc: "The wrist point is traditionally worked to ease feelings of stiffness and tiredness in the hands and forearms.",
    },
    {
        id: "kshipra",
        name: "Kshipra Marma",
        region: "Web of the hand",
        x: 156,
        y: 206,
        desc: "A traditionally 'quick-responding' point in the web of the hand, classically included for general relaxation.",
    },
    {
        id: "janu",
        name: "Janu Marma",
        region: "Knee joint",
        x: 84,
        y: 298,
        desc: "The knee point is traditionally associated with comfortable movement and a feeling of ease around the joint.",
    },
    {
        id: "talahridaya",
        name: "Talahridaya Marma",
        region: "Centre of the sole",
        x: 116,
        y: 380,
        desc: "The centre of the sole is traditionally worked for a grounded, settled feeling through the legs and feet.",
    },
];

const Figure = ({ active, onSelect }) => (
    <svg viewBox="0 0 200 400" className="mx-auto h-[420px] w-auto md:h-[520px]" role="img" aria-label="Stylized human figure showing selected marma point locations">
        <g fill="#2D4A3E" opacity="0.14">
            <circle cx="100" cy="40" r="26" />
            <rect x="90" y="62" width="20" height="16" rx="8" />
            <rect x="62" y="78" width="76" height="122" rx="30" />
            <rect x="36" y="84" width="16" height="112" rx="8" />
            <rect x="148" y="84" width="16" height="112" rx="8" />
            <circle cx="44" cy="204" r="11" />
            <circle cx="156" cy="204" r="11" />
            <rect x="68" y="198" width="64" height="36" rx="18" />
            <rect x="74" y="232" width="20" height="138" rx="10" />
            <rect x="106" y="232" width="20" height="138" rx="10" />
            <ellipse cx="84" cy="380" rx="16" ry="9" />
            <ellipse cx="116" cy="380" rx="16" ry="9" />
        </g>
        {POINTS.map((p) => {
            const isActive = active.id === p.id;
            return (
                <g
                    key={p.id}
                    onClick={() => onSelect(p)}
                    className="cursor-pointer"
                    data-testid={`marma-dot-${p.id}`}
                >
                    {isActive && (
                        <circle cx={p.x} cy={p.y} r="7" fill="none" stroke="#D4AF37" strokeWidth="1.5" className="animate-pulse-ring" style={{ transformOrigin: `${p.x}px ${p.y}px` }} />
                    )}
                    <circle
                        cx={p.x}
                        cy={p.y}
                        r={isActive ? 7 : 5}
                        fill={isActive ? "#D4AF37" : "#C47D57"}
                        stroke="#F8F6F0"
                        strokeWidth="2"
                        className="transition-all duration-300"
                    />
                </g>
            );
        })}
    </svg>
);

export default function MarmaPoints() {
    const [active, setActive] = useState(POINTS[2]);

    return (
        <section id="marma-points" data-testid="marma-points-section" className="bg-cream py-20 md:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    overline="Marma Points"
                    title="Explore the Vital Points"
                    testId="marma-points-heading"
                >
                    Classical Ayurvedic texts describe 107 marma points. Tap a point on the figure to read
                    how tradition describes it — a gentle introduction, not a treatment map.
                </SectionHeading>

                <div className="mt-14 grid items-center gap-12 lg:grid-cols-2">
                    <Reveal className="rounded-3xl border border-sand bg-bone p-6 md:p-10">
                        <Figure active={active} onSelect={setActive} />
                    </Reveal>

                    <div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.id}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="rounded-3xl bg-forest p-8 text-cream md:p-10"
                                data-testid="marma-info-panel"
                            >
                                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                                    {active.region}
                                </p>
                                <h3 className="mt-3 font-serif text-3xl font-medium">{active.name}</h3>
                                <p className="mt-4 text-sm leading-relaxed text-cream/80 md:text-base">
                                    {active.desc}
                                </p>
                            </motion.div>
                        </AnimatePresence>

                        <div className="mt-6 flex flex-wrap gap-2.5">
                            {POINTS.map((p) => (
                                <button
                                    key={p.id}
                                    data-testid={`marma-select-${p.id}`}
                                    onClick={() => setActive(p)}
                                    className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-[background-color,color,border-color] duration-300 ${
                                        active.id === p.id
                                            ? "border-forest bg-forest text-cream"
                                            : "border-sand bg-transparent text-moss hover:border-terracotta hover:text-ink"
                                    }`}
                                >
                                    {p.name}
                                </button>
                            ))}
                        </div>

                        <p className="mt-6 text-xs leading-relaxed text-moss/80">
                            Descriptions reflect classical Ayurvedic tradition. They are educational, not
                            medical claims, and sessions at our clinic are always adapted to your comfort
                            and health background.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
